'use client'
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import styles from './Watchlist.module.css'
import { ConnectorManager } from '../ConnectorManager'
import { TickerItem } from '../types'
import { FavoritesPopup } from './FavoritesPopup'
import { useGrowwFavorites } from './hooks/useGrowwFavorites'
import { useGrowwPolling } from './hooks/useGrowwPolling'
import { useGrowwInstruments } from './hooks/useGrowwInstruments'
import SegmentTabs from './SegmentTabs'
import { StarIcon as StarIconOutline } from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'
import {
  MagnifyingGlassIcon,
  ArrowsUpDownIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CurrencyDollarIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline'
import { AnimatedNumber } from '@/components/ui/animated-number'

/**
 * @uiComponent
 * @name Watchlist
 * @description The Watchlist component displays real-time market data for stocks on the Groww platform.
 * It allows users to view favorites, search through market segments (Popular, Cash, F&O), 
 * sort by various criteria, and navigate to individual stock pages. Features infinite scroll 
 * and real-time price updates with optimized polling.
 * @location Main left panel of the trading interface
 * @prop none - No external props, uses internal state management
 */

/**
 * @uiWorkflow
 * @name Add Stocks to Favorites on Groww
 * @description How to add stocks to your watchlist favorites for quick access
 * @platform groww
 * @step 1 Navigate to the Watchlist tab in the trading terminal [component: Watchlist] [action: navigate]
 * @step 2 Switch to the "All" tab to browse available stocks [component: Watchlist] [action: click]
 * @step 3 Choose a market segment: Popular, Cash, or F&O [component: SegmentTabs] [action: select]
 * @step 4 Use the search bar to find specific stocks by symbol [component: Watchlist] [action: input]
 * @step 5 Click the star icon next to any stock to add it to favorites [component: Watchlist] [action: click]
 * @step 6 Alternatively, click the "+ Add" button to open the favorites popup [component: Watchlist] [action: click]
 * @step 7 In the popup, search and select multiple stocks at once [component: FavoritesPopup] [action: select]
 * @step 8 Switch back to the "Favorites" tab to view your selected stocks [component: Watchlist] [action: click]
 */

/**
 * @uiWorkflow
 * @name Monitor Real-time Stock Prices on Groww
 * @description How to track live price movements and market data
 * @platform groww
 * @step 1 Open the Watchlist component in the trading interface [component: Watchlist] [action: navigate]
 * @step 2 Add stocks to favorites for focused monitoring [component: Watchlist] [action: click] [notes: Click star icons or use + Add button]
 * @step 3 View the "Favorites" tab for your selected stocks [component: Watchlist] [action: click]
 * @step 4 Observe real-time price updates with animated number changes [component: Watchlist] [action: observe]
 * @step 5 Sort stocks by price, change percentage, or volume for analysis [component: Watchlist] [action: click] [notes: Click on column headers or filter chips]
 * @step 6 Click on any stock symbol to view detailed charts and trading options [component: Watchlist] [action: click]
 * @step 7 Use the search function to quickly find specific stocks [component: Watchlist] [action: input]
 */

/**
 * @uiWorkflow
 * @name Browse Market Segments on Groww
 * @description How to explore different market categories and segments
 * @platform groww
 * @step 1 Navigate to the Watchlist and select the "All" tab [component: Watchlist] [action: click]
 * @step 2 Choose "Popular" segment to view trending stocks [component: SegmentTabs] [action: click]
 * @step 3 Switch to "Cash" segment to browse equity stocks [component: SegmentTabs] [action: click] [notes: Shows EQ and BE series stocks]
 * @step 4 Select "F&O" segment for futures and options instruments [component: SegmentTabs] [action: click]
 * @step 5 Use infinite scroll to load more stocks in each segment [component: Watchlist] [action: scroll]
 * @step 6 Apply sorting and filtering to find relevant instruments [component: Watchlist] [action: click]
 * @step 7 Click on any instrument to start trading [component: Watchlist] [action: click]
 */

/** Tabs at the top: Favorites, All */
const TABS = ['Favorites', 'All']
const SEGMENTS = ['POPULAR', 'CASH', 'FNO']
const PAGE_SIZE = 100

// In your Watchlist component
import { useRouter } from 'next/navigation'

// Access the global poll coordinator for adding pending symbols
declare global {
  interface Window {
    GLOBAL_POLL_COORDINATOR?: {
      pendingSymbols: Set<string>;
      [key: string]: any;
    };
  }
}

// Just after your other declarations and before the component definition:
// Make the global coordinator accessible if we're on the client side
if (typeof window !== 'undefined') {
  // This will be populated by the useGrowwPolling hook
  window.GLOBAL_POLL_COORDINATOR = window.GLOBAL_POLL_COORDINATOR || {
    pendingSymbols: new Set<string>(),
    lastPollTime: 0,
    isPolling: false,
    activePollers: 0,
    symbolsBeingPolled: new Set<string>(),
    subscribers: new Set()
  };
}

export default function Watchlist() {
  // Add router
  const router = useRouter()
  const [activeTab, setActiveTab] = useState(TABS[0])
  const [activeSegment, setActiveSegment] = useState(SEGMENTS[0])
  const [tickers, setTickers] = useState<TickerItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [allSymbols, setAllSymbols] = useState<string[]>([])
  const [isPopupOpen, setIsPopupOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortConfig, setSortConfig] = useState<{
    key: keyof TickerItem | 'symbol'
    direction: 'asc' | 'desc'
  }>({
    key: 'volume',
    direction: 'desc'
  })

  // Infinite scroll state
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
  const [loadingMore, setLoadingMore] = useState(false)
  const sentinelRef = useRef<HTMLTableRowElement>(null)
  
  // Store visible symbols in a ref to avoid dependency cycles
  const visibleSymbolsRef = useRef<string[]>([])

  const connectorManager = ConnectorManager.getInstance()

  // Use our custom hooks
  const { favorites, addFavorite, removeFavorite, toggleFavorite } = useGrowwFavorites()
  const { instruments, isLoading: isInstrumentsLoading, error: instrumentsError, changeSegment } = useGrowwInstruments()

  // Function to get visible symbols based on current view - store in ref to avoid re-renders
  const updateVisibleSymbols = useCallback(() => {
    let symbols: string[] = [];
    
    if (activeTab === 'Favorites') {
      symbols = favorites;
    } else {
      if (activeSegment === 'POPULAR') {
        // For popular tab, just return visible symbols from our tickers list
        symbols = tickers
          .slice(0, visibleCount)
          .map(t => t.originalSymbol);
      } else if (activeSegment === 'CASH' || activeSegment === 'FNO') {
        // Get currently visible instruments based on what's loaded so far
        if (instruments.length > 0) {
          // Filter relevant instruments
          const segmentInstruments = instruments
            .filter(instr => instr.segment === activeSegment)
            .filter(instr => activeSegment !== 'CASH' || (instr.series === 'EQ' || instr.series === 'BE'))
            .slice(0, visibleCount);
          
          symbols = segmentInstruments.map(instr => instr.trading_symbol);
        }
      }
    }
    
    // Update the ref with current visible symbols
    visibleSymbolsRef.current = symbols;
    return symbols;
  }, [activeTab, activeSegment, favorites, tickers, visibleCount, instruments]);

  // Update visible symbols whenever dependencies change
  useEffect(() => {
    updateVisibleSymbols();
  }, [updateVisibleSymbols]);
  
  // Use polling for real-time updates instead of WebSockets (Groww doesn't provide WebSockets)
  const { isConnected, error: pollingError } = useGrowwPolling({
    onTickerUpdate: (tickersData) => {
      if (tickersData.length === 0) return;
      
      console.log(`Received price updates for ${tickersData.length} instruments`);
      
      // Update existing tickers with new data instead of replacing the entire list
      setTickers(prevTickers => {
        // Create a map of existing tickers for quick lookup
        const tickerMap = new Map(prevTickers.map(ticker => [ticker.originalSymbol, ticker]))
        
        // Update existing tickers with new data
        tickersData.forEach(newTicker => {
          if (tickerMap.has(newTicker.originalSymbol)) {
            const existingTicker = tickerMap.get(newTicker.originalSymbol)!
            // Only update price and volume if they're non-zero, preserve existing change percent if new one is zero
            tickerMap.set(newTicker.originalSymbol, {
              ...existingTicker,
              price: newTicker.price || existingTicker.price,
              priceChangePercent: newTicker.priceChangePercent !== 0 ? 
                newTicker.priceChangePercent : existingTicker.priceChangePercent,
              volume: newTicker.volume || existingTicker.volume
            })
          } else {
            // Add new ticker if it doesn't exist
            tickerMap.set(newTicker.originalSymbol, newTicker)
          }
        })
        
        // Convert map back to array and return
        return Array.from(tickerMap.values())
      })
      
      // Update all symbols separately (avoid updating on every price change)
      const newSymbols = tickersData.map(ticker => ticker.originalSymbol);
      const hasNewSymbols = newSymbols.some(symbol => !allSymbols.includes(symbol));
      
      if (hasNewSymbols) {
        setAllSymbols(prev => [...new Set([...prev, ...newSymbols])]);
      }
    },
    pollingInterval: 10000, // Poll every 10 seconds to avoid excessive API calls
    symbols: visibleSymbolsRef.current, // Use the ref instead of the function to avoid cycles
    batchSize: 50 // Process 50 symbols at a time
  })

  // Handle segment change
  const handleSegmentChange = (segment: string) => {
    setActiveSegment(segment);
    setVisibleCount(PAGE_SIZE);
    setSearchTerm('');
    
    // Only fetch data for the selected segment
    if (segment !== 'POPULAR') {
      changeSegment(segment);
    } else {
      fetchTickers(); // Fetch popular tickers for POPULAR segment
    }
  }

  // Fetch tickers on mount / tab change / segment change
  useEffect(() => {
    if (activeTab === 'All') {
      handleSegmentChange(activeSegment);
    } else {
      fetchTickers();
    }
  }, [activeTab]);

  // Re-fetch when favorites change, but only if we're on the Favorites tab
  useEffect(() => {
    if (activeTab === 'Favorites') {
      fetchTickers();
    }
  }, [favorites]);

  // Add this function to handle symbol click
  const handleSymbolClick = (symbol: string, exchange: string) => {
    const exchangeSuffix = exchange.toLowerCase() === 'nse' ? 'ns' : exchange.toLowerCase() === 'bse' ? 'bs' : 'ns';
    window.location.href = `/${symbol.toLowerCase()}.${exchangeSuffix}`;
  }

  const fetchTickers = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Get the Groww connector
      const growwConnector = connectorManager.getConnector('groww');

      if (!growwConnector) {
        throw new Error('Groww connector not found or not connected');
      }

      // Fetch ticker data from the connector with proper typing
      const rawTickersData = await growwConnector.getTickers();
      
      // Transform the data to match the expected TickerItem format
      const tickersData: TickerItem[] = rawTickersData.map(ticker => ({
        symbol: ticker.symbol.toLowerCase(),
        originalSymbol: ticker.symbol,
        price: ticker.price || 0,
        priceChangePercent: ticker.priceChangePercent || 0,
        volume: ticker.volume || 0,
        exchange: ticker.exchange || 'NSE'
      }));

      // Extract all symbols for the search functionality
      setAllSymbols(tickersData.map(ticker => ticker.originalSymbol));

      // Set tickers
      setTickers(tickersData);
    } catch (err: any) {
      console.error('Error fetching tickers:', err);
      setError(
        err.message ||
          'Failed to fetch market data. Please ensure you are connected to Groww.'
      );
    } finally {
      setIsLoading(false);
    }
  }

  const sortTickers = (tickersToSort: TickerItem[]) => {
    const sortedTickers = [...tickersToSort].sort((a, b) => {
      if (sortConfig.key === 'symbol') {
        return sortConfig.direction === 'asc'
          ? a.originalSymbol.localeCompare(b.originalSymbol)
          : b.originalSymbol.localeCompare(a.originalSymbol);
      }

      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (sortConfig.direction === 'asc') {
        return aValue < bValue ? -1 : 1;
      } else {
        return aValue > bValue ? -1 : 1;
      }
    });

    return sortedTickers;
  }

  const handleSort = (key: keyof TickerItem | 'symbol') => {
    let direction: 'asc' | 'desc' = 'desc';

    if (sortConfig.key === key) {
      direction = sortConfig.direction === 'asc' ? 'desc' : 'asc';
    }

    setSortConfig({ key, direction });
  }

  const handleAddFavorite = (symbol: string) => {
    addFavorite(symbol);
    setIsPopupOpen(false);
  }

  // Compute filtered tickers based on activeTab, favorites, and searchTerm
  const filteredTickers = useMemo(() => {
    let list: TickerItem[] = [...tickers];
    
    // First filter by active tab
    if (activeTab === 'Favorites') {
      list = list.filter(ticker => favorites.includes(ticker.originalSymbol));
    } else if (activeTab === 'All') {
      if (activeSegment === 'CASH') {
        // Convert instruments to TickerItem format and merge with price data
        const cashInstruments = instruments.filter(instr => 
          instr.segment === 'CASH' && 
          (instr.series === 'EQ' || instr.series === 'BE')
        );
        
        console.log(`Found ${cashInstruments.length} instruments in CASH segment with EQ/BE series`);
        
        if (cashInstruments.length > 0) {
          // Create a map of tickers for lookup (use original symbol as key)
          const tickerMap = new Map(tickers.map(t => [t.originalSymbol, t]));
          
          // We'll use a batch fetching approach for efficiency
          const symbolsToFetch: string[] = [];
          const generatedItems: TickerItem[] = [];
          
          // First pass - use existing tickers and identify ones we need to fetch
          cashInstruments.forEach(instr => {
            const tradingSymbol = instr.trading_symbol;
            const ticker = tickerMap.get(tradingSymbol);
            
            if (ticker) {
              // We already have price data for this instrument
              generatedItems.push(ticker);
            } else {
              // Create a placeholder with default values
              generatedItems.push({
                symbol: tradingSymbol.toLowerCase(),
                originalSymbol: tradingSymbol,
                price: 0,
                priceChangePercent: 0,
                volume: 0,
                exchange: instr.exchange || 'NSE'
              });
              
              // Add to list of symbols to fetch
              symbolsToFetch.push(tradingSymbol);
            }
          });
          
          // Filter out holdings with null or empty trading_symbol
          const validHoldings = cashInstruments.filter((holding: any) => holding.trading_symbol && holding.trading_symbol.trim() !== '') || [];

          // Change the condition to check if data is already loaded
          if (symbolsToFetch.length > 0) {
            console.log(`Need to fetch prices for ${symbolsToFetch.length} additional symbols in CASH tab`);
            
            // Add these symbols to the pending symbols list for the polling hook to pick up
            // This is non-blocking - the polling system will fetch them on next cycle
            symbolsToFetch.forEach(symbol => {
              window.GLOBAL_POLL_COORDINATOR?.pendingSymbols?.add(symbol);
            });
          }
          
          // Use our generated items
          list = generatedItems;
          
          console.log(`Generated ${list.length} ticker items for CASH segment`);
        }
      } else if (activeSegment === 'FNO') {
        // Filter for FNO instruments - similar approach as CASH
        const fnoInstruments = instruments.filter(instr => instr.segment === 'FNO');
        
        console.log(`Found ${fnoInstruments.length} instruments in FNO segment`);
        
        if (fnoInstruments.length > 0) {
          // Create a map of tickers for lookup
          const tickerMap = new Map(tickers.map(t => [t.originalSymbol, t]));
          
          // We'll use a batch fetching approach for efficiency
          const symbolsToFetch: string[] = [];
          const generatedItems: TickerItem[] = [];
          
          // First pass - use existing tickers and identify ones we need to fetch
          fnoInstruments.forEach(instr => {
            const tradingSymbol = instr.trading_symbol;
            const ticker = tickerMap.get(tradingSymbol);
            
            if (ticker) {
              // We already have price data for this instrument
              generatedItems.push(ticker);
            } else {
              // Create a placeholder with default values
              generatedItems.push({
                symbol: tradingSymbol.toLowerCase(),
                originalSymbol: tradingSymbol,
                price: 0,
                priceChangePercent: 0,
                volume: 0,
                exchange: instr.exchange || 'NSE'
              });
              
              // Add to list of symbols to fetch
              symbolsToFetch.push(tradingSymbol);
            }
          });
          
          // Filter out holdings with null or empty trading_symbol
          const validHoldings = fnoInstruments.filter((holding: any) => holding.trading_symbol && holding.trading_symbol.trim() !== '') || [];

          // Change the condition to check if data is already loaded
          if (symbolsToFetch.length > 0) {
            console.log(`Need to fetch prices for ${symbolsToFetch.length} additional symbols in FNO tab`);
            
            // Add these symbols to the pending symbols list for the polling hook to pick up
            symbolsToFetch.forEach(symbol => {
              window.GLOBAL_POLL_COORDINATOR?.pendingSymbols?.add(symbol);
            });
          }
          
          // Use our generated items
          list = generatedItems;
          
          console.log(`Generated ${list.length} ticker items for FNO segment`);
        }
      }
      // POPULAR segment uses the default tickers list (all tickers)
    }
    
    // Then filter by search term if present
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      list = list.filter(ticker => ticker.originalSymbol.toLowerCase().includes(term));
    }
    
    // Apply sorting
    return sortTickers(list);
  }, [tickers, activeTab, activeSegment, favorites, searchTerm, sortConfig, instruments]);

  // Slice for pagination (infinite scroll)
  const pagedTickers = useMemo(() => {
    return filteredTickers.slice(0, visibleCount);
  }, [filteredTickers, visibleCount]);

  // Reset visibleCount when tab, segment, search or sort changes
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [activeTab, activeSegment, searchTerm, sortConfig]);

  // Infinite scroll using Intersection Observer
  useEffect(() => {
    const onScroll = () => {
      if (
        !loadingMore &&
        visibleCount < filteredTickers.length &&
        sentinelRef.current
      ) {
        const rect = sentinelRef.current.getBoundingClientRect();
        if (rect.top < window.innerHeight + 50) {
          setLoadingMore(true);
          setVisibleCount(vc =>
            Math.min(vc + PAGE_SIZE, filteredTickers.length)
          );
          // Small delay so spinner shows
          setTimeout(() => setLoadingMore(false), 300);
        }
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [loadingMore, visibleCount, filteredTickers.length]);

  function renderTabContent() {
    if (isLoading || (activeTab === 'All' && activeSegment !== 'POPULAR' && isInstrumentsLoading)) {
      return (
        <div className={styles.loadingState}>
          <div className={styles.spinner}></div>
          <p>Loading market data...</p>
        </div>
      );
    }

    if (error || (activeTab === 'All' && activeSegment !== 'POPULAR' && instrumentsError)) {
      return (
        <div className={styles.errorState}>
          <p>{error || instrumentsError}</p>
          <button onClick={fetchTickers} className={styles.retryButton}>
            Retry
          </button>
        </div>
      );
    }

    if (pagedTickers.length === 0) {
      return (
        <div className={styles.emptyState}>
          {activeTab === 'Favorites' ? (
            <>
              <p>No favorites added yet</p>
              <button
                onClick={() => setIsPopupOpen(true)}
                className={styles.addButton}
              >
                Add Favorites
              </button>
            </>
          ) : (
            <p>No data available</p>
          )}
        </div>
      );
    }

    return (
      <>
        <div className={styles.tableControls}>
          <div className={styles.searchContainer}>
            <MagnifyingGlassIcon
              className={styles.searchIcon}
              width={18}
              height={18}
            />
            <input
              type="text"
              placeholder="Search symbols..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>

          <div className={styles.filterChips}>
            <div
              className={`${styles.filterChip} ${sortConfig.key === 'symbol' ? styles.active : ''}`}
              onClick={() => handleSort('symbol')}
            >
              Name
              {sortConfig.key === 'symbol' &&
                (sortConfig.direction === 'asc' ? (
                  <ArrowUpIcon className={styles.chipIcon} />
                ) : (
                  <ArrowDownIcon className={styles.chipIcon} />
                ))}
            </div>

            <div
              className={`${styles.filterChip} ${sortConfig.key === 'price' ? styles.active : ''}`}
              onClick={() => handleSort('price')}
            >
              Price
              <CurrencyDollarIcon className={styles.chipIcon} />
              {sortConfig.key === 'price' &&
                (sortConfig.direction === 'asc' ? (
                  <ArrowUpIcon className={styles.chipIcon} />
                ) : (
                  <ArrowDownIcon className={styles.chipIcon} />
                ))}
            </div>

            <div
              className={`${styles.filterChip} ${sortConfig.key === 'priceChangePercent' ? styles.active : ''}`}
              onClick={() => handleSort('priceChangePercent')}
            >
              Change %
              {sortConfig.key === 'priceChangePercent' &&
                (sortConfig.direction === 'asc' ? (
                  <ArrowUpIcon className={styles.chipIcon} />
                ) : (
                  <ArrowDownIcon className={styles.chipIcon} />
                ))}   
            </div>

            <div
              className={`${styles.filterChip} ${sortConfig.key === 'volume' ? styles.active : ''}`}
              onClick={() => handleSort('volume')}
            >
              Volume
              <ChartBarIcon className={styles.chipIcon} />
              {sortConfig.key === 'volume' &&
                (sortConfig.direction === 'asc' ? (
                  <ArrowUpIcon className={styles.chipIcon} />
                ) : (
                  <ArrowDownIcon className={styles.chipIcon} />
                ))}
            </div>
          </div>

          {filteredTickers.length > 0 && (
            <div className={styles.resultsCount}>
              {filteredTickers.length} results
            </div>
          )}
        </div>

        <div className={styles.tickersTable}>
          <table>
            <tbody>
              {pagedTickers.map(ticker => (
                <tr key={ticker.originalSymbol} className={styles.tickerRow}>
                  <td
                    className={`${styles.symbolCell} ${styles.clickable}`}
                    onClick={() => handleSymbolClick(ticker.originalSymbol, ticker.exchange)}
                  >
                    {ticker.originalSymbol}
                  </td>
                  <td className={`${styles.priceCell} ${ticker.price === 0 ? styles.shimmerEffect : ''}`}>
                    {ticker.price === 0 ? (
                      <div className={styles.shimmerContainer}>
                        <div className={styles.shimmerBox}></div>
                      </div>
                    ) : (
                      <>
                        ₹<AnimatedNumber
                          value={ticker.price}
                          formatOptions={{
                            minimumFractionDigits: ticker.price < 1 ? 8 : 2,
                            maximumFractionDigits: ticker.price < 1 ? 8 : 2
                          }}
                          digitHeight={20}
                          duration={0.3}
                        />
                      </>
                    )}
                  </td>
                  <td
                    className={`${styles.changeCell} ${
                      ticker.price === 0 
                        ? styles.shimmerEffect 
                        : ticker.priceChangePercent >= 0 
                          ? styles.profit 
                          : styles.loss
                    }`}
                  >
                    {ticker.price === 0 ? (
                      <div className={styles.shimmerContainer}>
                        <div className={styles.shimmerBox}></div>
                      </div>
                    ) : (
                      <>
                        <AnimatedNumber
                          value={ticker.priceChangePercent}
                          formatOptions={{
                            signDisplay: 'exceptZero',
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          }}
                          digitHeight={20}
                          duration={0.3}
                        />%
                      </>
                    )}
                  </td>
                  <td className={`${styles.volumeCell} ${ticker.price === 0 ? styles.shimmerEffect : ''}`}>
                    {ticker.price === 0 ? (
                      <div className={styles.shimmerContainer}>
                        <div className={styles.shimmerBox}></div>
                      </div>
                    ) : (
                      <>₹{ticker.volume.toLocaleString()}</>
                    )}
                  </td>
                  <td className={styles.favoriteCell}>
                    <button
                      className={styles.favoriteButton}
                      onClick={() => toggleFavorite(ticker.originalSymbol)}
                      title={
                        favorites.includes(ticker.originalSymbol)
                          ? 'Remove from favorites'
                          : 'Add to favorites'
                      }
                    >
                      {favorites.includes(ticker.originalSymbol) ? (
                        <StarIconSolid
                          className={styles.starIconFilled}
                          width={18}
                          height={18}
                        />
                      ) : (
                        <StarIconOutline
                          className={styles.starIcon}
                          width={18}
                          height={18}
                        />
                      )}
                    </button>
                  </td>
                </tr>
              ))}

              {/* Sentinel row for infinite scroll */}
              <tr ref={sentinelRef} style={{ height: 1 }}>
                <td colSpan={5} />
              </tr>

              {/* Loading more row */}
              {loadingMore && (
                <tr>
                  <td colSpan={5} className={styles.loadingState}>
                    <div className={styles.spinner}></div>
                    <span style={{ marginLeft: 8 }}>Loading more...</span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </>
    );
  }

  return (
    <div className={`${styles.watchlistContainer} ${styles.themeVariables}`}>
      <div className={styles.tabHeader}>
        {TABS.map(tab => (
          <div
            key={tab}
            className={`${styles.tab} ${tab === activeTab ? styles.active : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </div>
        ))}
        <button
          className={styles.addFavoriteButton}
          onClick={() => setIsPopupOpen(true)}
        >
          + Add
        </button>
      </div>
      
      {/* Show segment tabs only when All tab is active */}
      {activeTab === 'All' && (
        <SegmentTabs 
          activeSegment={activeSegment} 
          onSegmentChange={handleSegmentChange} 
          segments={SEGMENTS}
        />
      )}
      
      <div className={styles.tabContent}>{renderTabContent()}</div>

      <FavoritesPopup
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        onAddFavorite={handleAddFavorite}
        currentFavorites={favorites}
        availableSymbols={allSymbols}
      />
    </div>
  )
}
